// @ts-nocheck
/**
 * A simple modal custom element.
 *
 * @example
 * <rt-modal title="Modal Title">
 *   Content modal here
 * </rt-modal>
 *
 * // Open modal programmatically
 * document.querySelector('rt-modal').open();
 *
 * // Close modal programmatically
 * document.querySelector('rt-modal').close();
 *
 * @fires rt-modal:open - When the modal is opened
 * @fires rt-modal:close - When the modal is closed
 */
const RT_MODAL_STYLES = `
      rt-modal {
        display: none;
        position: fixed;
        inset: 0;
        z-index: 1000;
        align-items: center;
        justify-content: center;
      }

      rt-modal[open] {
        display: flex;
      }

      .rt-modal__backdrop {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      rt-modal[open] .rt-modal__backdrop {
        opacity: 1;
      }

      .rt-modal__container {
        position: relative;
        background: white;
        border-radius: 8px;
        max-width: 500px;
        width: 90%;
        max-height: 90vh;
        overflow: auto;
        transform: scale(0.95) translateY(-10px);
        opacity: 0;
        transition: transform 0.3s ease, opacity 0.3s ease;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
      }

      rt-modal[open] .rt-modal__container {
        transform: scale(1) translateY(0);
        opacity: 1;
      }

      .rt-modal__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px 20px;
        border-bottom: 1px solid #e5e5e5;
      }

      .rt-modal__title {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        line-height: 1.4;
      }

      .rt-modal__close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: none;
        background: transparent;
        cursor: pointer;
        border-radius: 4px;
        color: inherit;
        transition: background-color 0.2s ease;
      }

      .rt-modal__close:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }

      .rt-modal__close:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
      }

      .rt-modal__content {
        padding: 20px;
      }
    `;

class RtModal extends HTMLElement {
  static tagName = 'rt-modal';

  static get observedAttributes() {
    return ['title', 'open'];
  }

  constructor() {
    super();
    this._isOpen = false;
    this._previousActiveElement = null;
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._handleBackdropClick = this._handleBackdropClick.bind(this);
  }

  connectedCallback() {
    this._render();
    this._setupEventListeners();

    // Check if modal should be open initially
    if (this.hasAttribute('open')) {
      this.open();
    }
  }

  disconnectedCallback() {
    this._removeEventListeners();
  }

  /**
   * Called when an observed attribute has been added, removed, or changed.
   * @param {string} name - The name of the attribute that changed.
   * @param {string|null} oldValue - The previous value of the attribute.
   * @param {string|null} newValue - The new value of the attribute.
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) {
      return;
    }

    if (name === 'title') {
      const titleEl = this.querySelector('.rt-modal__title');
      if (titleEl) {
        titleEl.textContent = newValue || '';
      }
    }

    if (name === 'open') {
      if (newValue !== null) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  _render() {
    const title = this.getAttribute('title') || '';
    const content = this.innerHTML;

    this.innerHTML = `
      <div class="rt-modal__backdrop" part="backdrop"></div>
      <div class="rt-modal__container" role="dialog" aria-modal="true" aria-labelledby="rt-modal-title-${this._uniqueId}" part="container">
        <header class="rt-modal__header" part="header">
          <h2 class="rt-modal__title" id="rt-modal-title-${this._uniqueId}" part="title">${title}</h2>
          <button type="button" class="rt-modal__close" aria-label="Close modal" part="close-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </header>
        <div class="rt-modal__content" part="content">${content}</div>
      </div>
    `;

    this._injectStyles();
  }

  _injectStyles() {
    if (document.getElementById('rt-modal-styles')) {
      return;
    }

    const styles = document.createElement('style');
    styles.id = 'rt-modal-styles';
    styles.textContent = RT_MODAL_STYLES;
    document.head.appendChild(styles);
  }

  _setupEventListeners() {
    const closeBtn = this.querySelector('.rt-modal__close');
    const backdrop = this.querySelector('.rt-modal__backdrop');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    if (backdrop) {
      backdrop.addEventListener('click', this._handleBackdropClick);
    }
  }

  _removeEventListeners() {
    document.removeEventListener('keydown', this._handleKeyDown);
  }

  /**
   * Handles keydown events for accessibility.
   * @param {KeyboardEvent} event - The keyboard event
   */
  _handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }

    // Trap focus within modal
    if (event.key === 'Tab') {
      this._trapFocus(event);
    }
  }

  /**
   * Handles clicks on the backdrop to close the modal.
   * @param {MouseEvent} event - The mouse event
   */
  _handleBackdropClick(event) {
    if (event.target === this.querySelector('.rt-modal__backdrop')) {
      this.close();
    }
  }

  /***
   * Traps focus within the modal when open.
   * @param {KeyboardEvent} event - The keyboard event
   */
  _trapFocus(event) {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable?.focus();
    } else if (!event.shiftKey && document.activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable?.focus();
    }
  }

  get _uniqueId() {
    if (!this._id) {
      this._id = Math.random().toString(36).substring(2, 9);
    }
    return this._id;
  }

  /**
   * Opens the modal.
   */
  open() {
    if (this._isOpen) {
      return;
    }

    this._previousActiveElement = document.activeElement;
    this._isOpen = true;
    this.setAttribute('open', '');

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    // Add keyboard listeners
    document.addEventListener('keydown', this._handleKeyDown);

    // Focus the first focusable element or the container
    requestAnimationFrame(() => {
      const firstFocusable = this.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    });

    this.dispatchEvent(new CustomEvent('rt-modal:open', { bubbles: true, composed: true }));
  }

  /**
   * Closes the modal.
   */
  close() {
    if (!this._isOpen) {
      return;
    }

    this._isOpen = false;
    this.removeAttribute('open');

    // Unlock body scroll
    document.body.style.overflow = '';

    // Remove keyboard listeners
    document.removeEventListener('keydown', this._handleKeyDown);

    // Restore focus
    this._previousActiveElement?.focus();

    this.dispatchEvent(new CustomEvent('rt-modal:close', { bubbles: true, composed: true }));
  }

  /**
   * Toggles the modal open/closed state.
   */
  toggle() {
    if (this._isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Whether the modal is currently open.
   * @type {boolean}
   */
  get isOpen() {
    return this._isOpen;
  }
}

// Register the custom element
if (!customElements.get(RtModal.tagName)) {
  customElements.define(RtModal.tagName, RtModal);
}

export { RtModal };
